import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type AdminMarket = "pt" | "br";

const KEY = "hx_admin_market";
const EVENT = "hx-admin-market-change";

/** Market a scoped staff user is locked to (null = global, may switch). */
let lockedMarket: AdminMarket | null = null;
let lockLoaded = false;

async function loadLock() {
  const { data: { user } } = await supabase.auth.getUser();
  lockedMarket = null;
  if (user) {
    const { data } = await supabase.rpc("get_user_market_scope" as any, { _user_id: user.id });
    if (data === "pt" || data === "br") lockedMarket = data;
  }
  lockLoaded = true;
  if (lockedMarket) setAdminMarket(lockedMarket, true);
  else window.dispatchEvent(new CustomEvent(EVENT, { detail: getAdminMarket() }));
}
if (typeof window !== "undefined") {
  loadLock();
  supabase.auth.onAuthStateChange((evt) => { if (evt === "SIGNED_IN" || evt === "SIGNED_OUT") loadLock(); });
}

export function isAdminMarketLocked() { return lockedMarket !== null; }

export function getAdminMarket(): AdminMarket {
  if (lockedMarket) return lockedMarket;
  try {
    const stored = sessionStorage.getItem(KEY);
    if (stored === "pt" || stored === "br") return stored;
    return window.location.hostname.toLowerCase().includes("hexatechbrasil") ? "br" : "pt";
  } catch {
    return "pt";
  }
}

export function setAdminMarket(market: AdminMarket, force = false) {
  if (market !== "pt" && market !== "br") return;
  if (lockedMarket && !force) market = lockedMarket;
  try {
    sessionStorage.setItem(KEY, market);
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: market }));
}

/**
 * Admin viewing context (Portugal vs Brasil). Separate from the storefront
 * market (?market=br) and from the per-user market scope permission.
 * Defaults to "pt" so Portugal is never affected.
 */
export function useAdminMarket() {
  const [market, setMarket] = useState<AdminMarket>(getAdminMarket);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<AdminMarket>).detail;
      setMarket(detail || getAdminMarket());
    };
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  const switchMarket = useCallback((m: AdminMarket) => setAdminMarket(m), []);

  return { market, isBR: market === "br", switchMarket, locked: lockedMarket !== null, lockLoaded };
}

/** Stamp for inserts into market-scoped operational tables (active admin market). */
export function marketStamp(): { market: AdminMarket } {
  return { market: getAdminMarket() };
}

/** Filter a Supabase query builder by the active admin market (`market` column). */
export function applyAdminMarket<T extends { eq: (column: string, value: unknown) => T }>(query: T): T {
  return query.eq("market", getAdminMarket());
}
